/**
 * CarruselEliptico — GestiónEJP
 * Rotación elíptica continua · RAF · DOM directo
 * Cards minimalistas: ícono + nombre + CTA
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { tienePermiso } from '../auth/roles';
import '../style/components/CarruselEliptico.css';

// ─── Íconos SVG — módulos ─────────────────────────────────────────────────────

const IcoRTI = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const IcoART = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <path d="M12 9v4M12 17h.01"/>
  </svg>
);

const IcoClientes = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="3"/>
    <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
    <circle cx="17" cy="9" r="2.5"/>
    <path d="M21 21v-1.5a3.5 3.5 0 00-2.5-3.36"/>
  </svg>
);

const IcoRecepcion = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 19a9 9 0 0118 0"/>
    <path d="M3 19h18"/>
    <path d="M12 19v-3"/>
    <circle cx="12" cy="7" r="1.4" fill="currentColor" stroke="none"/>
  </svg>
);

// ─── Íconos SVG — navegación ──────────────────────────────────────────────────

const IcoChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
);

const IcoChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);

const IcoArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6"/>
  </svg>
);

// ─── Módulos ──────────────────────────────────────────────────────────────────

// Cada módulo declara el permiso (ver auth/roles.js) que lo habilita — así un
// usuario RTI, por ejemplo, ya no entra a ART desde el carrusel del Home.
const MODULOS = [
  {
    id:         'rti',
    nombre:     'RTI',
    icono:      <IcoRTI />,
    color:      '#2563eb',
    color2:     '#3b82f6',
    colorFondo: '#eff6ff',
    ruta:       '/rti',
    permiso:    'rti.ver',
  },
  {
    id:         'art',
    nombre:     'ART',
    icono:      <IcoART />,
    color:      '#b45309',
    color2:     '#d97706',
    colorFondo: '#fffbeb',
    ruta:       '/art',
    permiso:    'art.ver',
  },
  {
    id:         'clientes',
    nombre:     'Clientes',
    icono:      <IcoClientes />,
    color:      '#047857',
    color2:     '#059669',
    colorFondo: '#ecfdf5',
    ruta:       '/clientes',
    permiso:    'clientes.ver',
  },
  {
    id:         'recepcion',
    nombre:     'Recepción',
    icono:      <IcoRecepcion />,
    color:      '#7c3aed',
    color2:     '#8b5cf6',
    colorFondo: '#f5f3ff',
    ruta:       '/recepcion',
    permiso:    'recepcion.ver',
  },
];

// ─── Constantes de la elipse ──────────────────────────────────────────────────

const VELOCIDAD = 0.00028;             // rad/ms → ≈ 22 s por vuelta

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function calcPos(phase, i, radioX, angSep) {
  const ang     = phase + i * angSep;
  const cosA    = Math.cos(ang);
  const sinA    = Math.sin(ang);
  const zFactor = (cosA + 1) / 2;
  return {
    x:       sinA * radioX,
    zFactor,
    scale:   0.64 + Math.pow(zFactor, 1.3) * 0.36,
    opacity: 0.48 + Math.pow(zFactor, 1.1) * 0.52,
    zIndex:  Math.round(zFactor * 100),
  };
}

// ─── Componente ───────────────────────────────────────────────────────────────

const CarruselEliptico = () => {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const phaseRef   = useRef(0);
  const rafRef     = useRef(null);
  const lastTRef   = useRef(null);
  const snapRef    = useRef(null);
  const cardRefs   = useRef([]);
  const escenaRef  = useRef(null);
  const radioXRef  = useRef(200);
  const prevIdxRef = useRef(0);
  const nRef       = useRef(1);
  const angSepRef  = useRef(2 * Math.PI);

  const modulosVisibles = useMemo(
    () => MODULOS.filter((m) => tienePermiso(user?.rol, m.permiso)),
    [user?.rol]
  );

  const [indiceCentro, setIndiceCentro] = useState(0);

  // N y separación angular dependen de cuántos módulos ve el usuario logueado.
  useEffect(() => {
    nRef.current = modulosVisibles.length || 1;
    angSepRef.current = (2 * Math.PI) / nRef.current;
    phaseRef.current = 0;
    prevIdxRef.current = 0;
    setIndiceCentro(0);
  }, [modulosVisibles.length]);

  // Adapta radioX al ancho real del contenedor
  useEffect(() => {
    const actualizar = () => {
      if (!escenaRef.current) return;
      const w = escenaRef.current.offsetWidth;
      radioXRef.current = Math.min(230, Math.max(110, w / 2 - 145));
    };
    actualizar();
    window.addEventListener('resize', actualizar);
    return () => window.removeEventListener('resize', actualizar);
  }, []);

  // Loop RAF — actualiza DOM directamente
  const animar = useCallback((ts) => {
    const dt = lastTRef.current !== null ? Math.min(ts - lastTRef.current, 50) : 0;
    lastTRef.current = ts;

    if (snapRef.current !== null) {
      const snap = snapRef.current;
      snap.progress = Math.min(1, snap.progress + 0.055);
      phaseRef.current = snap.startPhase + snap.delta * easeOutCubic(snap.progress);
      if (snap.progress >= 1) {
        phaseRef.current = snap.startPhase + snap.delta;
        snapRef.current  = null;
      }
    } else {
      phaseRef.current -= VELOCIDAD * dt;
    }

    const n = nRef.current;
    const angSep = angSepRef.current;

    let maxCos = -Infinity, activeIdx = 0;
    for (let i = 0; i < n; i++) {
      const c = Math.cos(phaseRef.current + i * angSep);
      if (c > maxCos) { maxCos = c; activeIdx = i; }
    }

    const rx = radioXRef.current;
    for (let i = 0; i < n; i++) {
      const el = cardRefs.current[i];
      if (!el) continue;
      const { x, scale, opacity, zIndex, zFactor } = calcPos(phaseRef.current, i, rx, angSep);
      el.style.transform = `translateX(${x.toFixed(1)}px) scale(${scale.toFixed(4)}) translateZ(0)`;
      el.style.opacity   = opacity.toFixed(4);
      el.style.zIndex    = zIndex;
      el.classList.toggle('ce-card--activa', zFactor > 0.82);
    }

    if (activeIdx !== prevIdxRef.current) {
      prevIdxRef.current = activeIdx;
      setIndiceCentro(activeIdx);
    }

    rafRef.current = requestAnimationFrame(animar);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animar);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [animar]);

  // Snap suave al índice objetivo
  const irA = useCallback((targetIdx) => {
    let angTarget = ((phaseRef.current + targetIdx * angSepRef.current) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    let delta = -angTarget;
    if (delta > Math.PI)  delta -= 2 * Math.PI;
    if (delta < -Math.PI) delta += 2 * Math.PI;
    snapRef.current = { startPhase: phaseRef.current, delta, progress: 0 };
  }, []);

  const irAnterior  = useCallback(() => irA((indiceCentro - 1 + nRef.current) % nRef.current), [irA, indiceCentro]);
  const irSiguiente = useCallback(() => irA((indiceCentro + 1) % nRef.current),      [irA, indiceCentro]);

  const moduloCentro = modulosVisibles[indiceCentro];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="ce-wrapper"
      aria-label="Carrusel de módulos del sistema"
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); irAnterior();  }
        if (e.key === 'ArrowRight') { e.preventDefault(); irSiguiente(); }
      }}
      tabIndex={0}
    >
      {/* Escena elíptica */}
      <div className="ce-escena" ref={escenaRef}>
        {modulosVisibles.map((m, i) => (
          <article
            key={m.id}
            ref={(el) => { cardRefs.current[i] = el; }}
            className="ce-card"
            style={{
              '--accent':    m.color,
              '--accent-2':  m.color2,
              '--accent-bg': m.colorFondo,
            }}
            onClick={() => {
              if (indiceCentro === i) navigate(m.ruta);
              else irA(i);
            }}
            role="button"
            tabIndex={indiceCentro === i ? 0 : -1}
            aria-label={
              indiceCentro === i
                ? `${m.nombre} — Presioná Enter para ingresar`
                : `Ir a ${m.nombre}`
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (indiceCentro === i) navigate(m.ruta);
                else irA(i);
              }
            }}
          >
            {/* Indicador de activo */}
            <div className="ce-card__badge-activo" aria-hidden="true" />

            {/* Ícono prominente */}
            <div className="ce-card__icono-wrap" aria-hidden="true">
              {m.icono}
            </div>

            {/* Solo el nombre */}
            <h3 className="ce-card__nombre">{m.nombre}</h3>

            {/* CTA */}
            <button
              className="ce-card__cta"
              onClick={(e) => { e.stopPropagation(); navigate(m.ruta); }}
              tabIndex={indiceCentro === i ? 0 : -1}
              type="button"
              aria-label={`Ingresar a ${m.nombre}`}
            >
              <span>Ingresar</span>
              <IcoArrow />
            </button>
          </article>
        ))}
      </div>

      {/* Controles: flechas + dots */}
      <nav className="ce-controles" aria-label="Navegación del carrusel">
        <button
          className="ce-nav-btn"
          onClick={irAnterior}
          aria-label="Módulo anterior"
          type="button"
        >
          <IcoChevronLeft />
        </button>

        <div className="ce-indicadores" role="tablist" aria-label="Módulos">
          {modulosVisibles.map((m, i) => (
            <button
              key={m.id}
              className={`ce-dot${i === indiceCentro ? ' ce-dot--activo' : ''}`}
              onClick={() => irA(i)}
              aria-label={`Ir a ${m.nombre}`}
              aria-selected={i === indiceCentro}
              role="tab"
              type="button"
            />
          ))}
        </div>

        <button
          className="ce-nav-btn"
          onClick={irSiguiente}
          aria-label="Módulo siguiente"
          type="button"
        >
          <IcoChevronRight />
        </button>
      </nav>

      {/* Nombre del módulo activo */}
      {moduloCentro && (
        <p className="ce-modulo-activo" aria-live="polite">
          <strong>{moduloCentro.nombre}</strong>
        </p>
      )}
    </div>
  );
};

export default CarruselEliptico;
